// Translation layer between the backend's error envelope and what the login
// screen needs to decide. Kept free of axios and React so it can be unit-tested
// directly — this is where the real branching logic lives.
//
// The backend answers errors in the shared envelope (md/core/errors.py):
//   { success: false,
//     error: { code, message, details: { fields: { password: [...] } } } }
// `code` is a stable, machine-readable contract; `message` is for humans.

export type AuthFailureCode =
  | "invalid_credentials" // wrong phone or password — also unknown/non-staff
  | "password_not_set" // internal user who has never enrolled -> wizard
  | "invalid_otp" // wrong or expired code
  | "not_internal" // valid code, but no dashboard access
  | "validation" // password rejected by the Django validators
  | "rate_limited" // 429
  | "forbidden"
  | "network" // request never reached the server
  | "unknown";

export interface AuthError {
  ok: false;
  code: AuthFailureCode;
  message: string;
  /** Field-scoped messages, already normalised to arrays. */
  fieldErrors?: Record<string, string[]>;
  /** Seconds; only meaningful when code === "rate_limited". */
  retryAfter?: number;
}

export interface AuthSuccess {
  ok: true;
  token?: string;
  refresh?: string;
  user?: any;
  message?: string;
}

export type AuthResult = AuthSuccess | AuthError;

// Backend `error.code` -> the union above. Anything unlisted falls through to
// the status-based mapping, so a new backend code degrades to a sensible generic
// rather than breaking the screen.
const CODE_MAP: Record<string, AuthFailureCode> = {
  INVALID_CREDENTIALS: "invalid_credentials",
  PASSWORD_NOT_SET: "password_not_set",
  INVALID_OTP: "invalid_otp",
  NOT_INTERNAL: "not_internal",
  VALIDATION_ERROR: "validation",
  MISSING_FIELDS: "validation",
  RATE_LIMITED: "rate_limited",
  OTP_LOGIN_DISABLED: "unknown",
};

const DEFAULT_MESSAGE: Record<AuthFailureCode, string> = {
  invalid_credentials: "Incorrect phone number or password.",
  password_not_set: "You have not set a password yet.",
  invalid_otp: "That code is incorrect or has expired.",
  not_internal: "This number does not have dashboard access.",
  validation: "Please check the details and try again.",
  rate_limited: "Too many attempts. Please try again later.",
  forbidden: "You do not have permission to do that.",
  network: "Could not reach the server. Check your connection and try again.",
  unknown: "Something went wrong. Please try again.",
};

function envelope(data: any): any {
  return data && typeof data === "object" ? data.error : undefined;
}

/** Human-readable message from a response body, if it carries a usable one. */
export function pickMessage(data: any): string | undefined {
  const err = envelope(data);
  if (err && typeof err.message === "string" && err.message) return err.message;
  // Legacy auth endpoints answer a bare JSON string ("Invalid OTP").
  if (typeof data === "string" && data && !data.startsWith("<")) return data;
  if (data && typeof data.detail === "string") return data.detail;
  return undefined;
}

/**
 * Field-scoped messages, normalised to `string[]`.
 *
 * Reads `error.details.fields`, which is where the standard handler puts them.
 * Values may arrive as a bare string rather than a list, so both are coerced.
 */
export function extractFieldErrors(
  data: any
): Record<string, string[]> | undefined {
  const fields = envelope(data)?.details?.fields;
  if (!fields || typeof fields !== "object") return undefined;

  const out: Record<string, string[]> = {};
  Object.entries(fields).forEach(([key, value]) => {
    const messages = (Array.isArray(value) ? value : [value])
      .filter((m) => typeof m === "string" && m)
      .map(String);
    if (messages.length) out[key] = messages;
  });
  return Object.keys(out).length ? out : undefined;
}

/**
 * Normalise anything axios can reject with into an AuthError.
 *
 * Order matters: an explicit backend `code` wins over any status-based guess,
 * because the server knows things the status cannot express (notably
 * "password not set", which is otherwise indistinguishable from a wrong one).
 */
export function toAuthError(error: any, fallback: string): AuthError {
  const response = error?.response;

  // No response at all: DNS failure, offline, CORS, timeout.
  if (!response) {
    return { ok: false, code: "network", message: DEFAULT_MESSAGE.network };
  }

  const { status, data, headers } = response;
  const fieldErrors = extractFieldErrors(data);
  const backendCode = envelope(data)?.code;

  let code: AuthFailureCode;
  if (backendCode && CODE_MAP[backendCode]) {
    code = CODE_MAP[backendCode];
  } else if (status === 429) {
    code = "rate_limited";
  } else if (fieldErrors) {
    code = "validation";
  } else if (status === 403) {
    code = "forbidden";
  } else if (status === 400 || status === 401 || status === 404) {
    code = "invalid_credentials";
  } else {
    code = "unknown";
  }

  // A 5xx body is a stack trace or an HTML error page — never show it to a user.
  const safeMessage =
    status >= 500 ? undefined : pickMessage(data) ?? undefined;

  const result: AuthError = {
    ok: false,
    code,
    message: safeMessage || fallback || DEFAULT_MESSAGE[code],
  };
  if (fieldErrors) result.fieldErrors = fieldErrors;
  if (code === "rate_limited") {
    result.retryAfter = Number(headers?.["retry-after"]) || 0;
  }
  return result;
}
